// An employer's bank account as a contribution source: selecting it means new
// cash funded by the employer, so only the employer side of the contribution
// applies. Mirrors ContributeBankAccountSourceRow, which covers the client's
// own accounts.
import React from 'react';
import {Box} from '@mui/material';
import {PlanRow} from '../CreateBuilder/BuilderSourceDestination/PlanRow';
import {PlanAccordionShell} from '../CreateBuilder/BuilderSourceDestination/PlanAccordionShell';
import {PlanValidationIcon} from '../CreateBuilder/BuilderSourceDestination/PlanValidationIcon';
import {usePlanValidationLevel} from '../CreateBuilder/BuilderSourceDestination/usePlanValidationLevel';
import {
  BankAccountRowStatus,
  BankAccountRowSummary,
} from '../CreateBuilder/BuilderSourceDestination/BankAccountRowSummary';
import {BankAccountDetails} from '../CreateBuilder/BuilderSourceDestination/BankAccountDetails';
import {ContributeEmployerBankAccount} from './useContributionPotsQuery';
import {BuilderContributeSourceFields} from './BuilderContributeSourceFields';

interface ContributeEmployerBankAccountSourceRowProps {
  bankAccount: ContributeEmployerBankAccount;
  isSelected: boolean;
  handleChange: (bankAccountId: string) => void;
}

export const ContributeEmployerBankAccountSourceRow: React.FC<
  ContributeEmployerBankAccountSourceRowProps
> = ({bankAccount, isSelected, handleChange}) => {
  const activeFormikKey = isSelected ? 'contribute.contributeFrom' : undefined;
  const {bgClass} = usePlanValidationLevel(activeFormikKey);

  return (
    <PlanRow
      id={bankAccount.id}
      reccId={null}
      curoId={bankAccount.curoId ?? undefined}
      isSelected={isSelected}
      isSubplan={false}
    >
      <PlanAccordionShell
        expanded={isSelected}
        isSelected={isSelected}
        isSoleParent
        validationLevel={bgClass}
        summaryChildren={
          <BankAccountRowSummary
            bankAccount={bankAccount}
            isSelected={isSelected}
            handleChange={() => handleChange(bankAccount.id)}
            ownerName={bankAccount.account?.name}
          />
        }
        summaryTrailing={
          <>
            <BankAccountRowStatus bankAccount={bankAccount} ownershipLabel="Employer" />
            {activeFormikKey && <PlanValidationIcon formikKeyPrefix={activeFormikKey} />}
          </>
        }
      >
        <BankAccountDetails bankAccount={bankAccount} />
        <Box mt={1} />
        <BuilderContributeSourceFields />
      </PlanAccordionShell>
    </PlanRow>
  );
};
